import { weekStart, localToday, addDays, isoOf } from "./biz.js";

const r2 = (n) => Math.round(n * 100) / 100;
const parse = (iso) => { const [y, m, d] = iso.split("-").map(Number); return new Date(y, m - 1, d); };

export function nextPayslipNumber(list, prefix = "PS-") {
  let max = 0;
  (list || []).forEach((p) => { const m = String(p.number || "").match(/(\d+)\s*$/); if (m) max = Math.max(max, +m[1]); });
  return `${prefix}${String(max + 1).padStart(4, "0")}`;
}

export function payPeriodPresets(today = localToday()) {
  const t = parse(today);
  const ws = weekStart(today);
  const lastWeekStart = addDays(ws, -7);
  const first = new Date(t.getFullYear(), t.getMonth(), 1);
  const lastMonthFirst = new Date(t.getFullYear(), t.getMonth() - 1, 1);
  const lastMonthLast = new Date(t.getFullYear(), t.getMonth(), 0);
  return [
    { id: "lastweek", label: "Last week", from: lastWeekStart, to: addDays(lastWeekStart, 6) },
    { id: "thisweek", label: "This week", from: ws, to: addDays(ws, 6) },
    { id: "lastmonth", label: "Last month", from: isoOf(lastMonthFirst), to: isoOf(lastMonthLast) },
    { id: "thismonth", label: "This month", from: isoOf(first), to: isoOf(new Date(t.getFullYear(), t.getMonth() + 1, 0)) },
  ];
}

// Hours that can go on a payslip: this person, in range, not already on one.
export function payableHours(hours, staffId, from, to, { includePaid = false } = {}) {
  return (hours || []).filter((h) => h.staffId === staffId && h.date >= from && h.date <= to && (includePaid || !h.payslipId));
}

// Overtime is weekly (Mon–Sun) hours beyond the person's threshold, paid at a multiplier.
// A week cut by the period boundary is judged on the hours inside the period.
export function computePayslip({ staff, hours, taxPct = 0, otherDeductions = [], employerCostPct }) {
  const rate = Number(staff.payRate) || 0;
  const threshold = Number(staff.overtimeAfter) || 0;
  const mult = Number(staff.overtimeMult) || 1.5;
  const weeks = {};
  hours.forEach((h) => { const w = weekStart(h.date); weeks[w] = (weeks[w] || 0) + (Number(h.hours) || 0); });
  let regular = 0, overtime = 0;
  Object.values(weeks).forEach((total) => {
    if (threshold > 0 && total > threshold) { regular += threshold; overtime += total - threshold; } else regular += total;
  });
  regular = r2(regular); overtime = r2(overtime);
  const regularPay = r2(regular * rate);
  const overtimePay = r2(overtime * rate * mult);
  const gross = r2(regularPay + overtimePay);
  const taxAmount = r2(gross * (Number(taxPct) || 0) / 100);
  const others = (otherDeductions || []).filter((d) => d.label?.trim() && Number(d.amount) > 0).map((d) => ({ label: d.label.trim(), amount: r2(Number(d.amount)) }));
  const deductionsTotal = r2(taxAmount + others.reduce((s, d) => s + d.amount, 0));
  // Employer-side cost on top of gross (payroll taxes, super, KiwiSaver, pension...). Not taken from the employee's pay.
  const empPct = Number(employerCostPct ?? staff.employerCostPct) || 0;
  const employerCost = r2(gross * empPct / 100);
  return { employerCostPct: empPct, employerCost, rate, overtimeMult: mult, overtimeAfter: threshold, regularHours: regular, overtimeHours: overtime, totalHours: r2(regular + overtime), regularPay, overtimePay, gross, taxPct: Number(taxPct) || 0, taxAmount, others, deductionsTotal, net: r2(gross - deductionsTotal), totalCost: r2(gross + employerCost) };
}

// Year-to-date gross/net for this person including payslips already issued this calendar year before `before`.
export function ytdFor(payslips, staffId, year, includeGross, includeNet, includeHours, beforeId) {
  const prior = (payslips || []).filter((p) => p.staffId === staffId && p.to.startsWith(String(year)) && p.id !== beforeId);
  return {
    gross: r2(prior.reduce((s, p) => s + p.calc.gross, 0) + includeGross),
    net: r2(prior.reduce((s, p) => s + p.calc.net, 0) + includeNet),
    hours: r2(prior.reduce((s, p) => s + p.calc.totalHours, 0) + includeHours),
  };
}

// Totals across payslips issued in a date range, for the payroll summary and for tax.
export function payrollSummary(payslips, range) {
  const list = (payslips || []).filter((p) => !range || (p.issuedDate >= range.start && p.issuedDate <= range.end));
  const sum = (f) => r2(list.reduce((t, p) => t + (f(p) || 0), 0));
  return {
    count: list.length, gross: sum((p) => p.calc.gross), taxWithheld: sum((p) => p.calc.taxAmount),
    otherDeductions: sum((p) => p.calc.deductionsTotal - p.calc.taxAmount), net: sum((p) => p.calc.net),
    employerCost: sum((p) => p.calc.employerCost), totalCost: sum((p) => p.calc.gross + (p.calc.employerCost || 0)), hours: sum((p) => p.calc.totalHours),
  };
}
